import { Link } from "@tanstack/react-router";
import { Wordmark } from "@/components/sox/Wordmark";
import type { ReactNode } from "react";
import { LEGAL, LEGAL_ROUTES } from "@/lib/legal";

export function LegalLayout({
  index,
  title,
  intro,
  children,
}: {
  index: string;
  title: string;
  intro: string;
  children: ReactNode;
}) {
  return (
    <main className="min-h-screen bg-white text-black">
      <header className="border-b-2 border-black">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-6 sm:px-6">
          <Link to="/" className="inline-flex items-center">
            <Wordmark className="h-7 w-auto text-black" />
          </Link>
          <Link
            to="/"
            className="font-mono text-xs uppercase tracking-widest hover:text-accent"
          >
            ← Back to store
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-4xl px-5 py-16 sm:px-6 lg:py-24">
        <p className="font-mono text-xs uppercase tracking-widest text-black/50">
          [{index}] Legal
        </p>
        <h1 className="mt-4 text-4xl font-extrabold uppercase leading-none tracking-tighter sm:text-6xl">
          {title}
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-black/70">{intro}</p>
        <p className="mt-4 font-mono text-xs uppercase tracking-widest text-black/40">
          Last updated: {LEGAL.lastUpdated}
        </p>

        <div className="mt-14 space-y-12">{children}</div>

        <nav className="mt-20 border-t-2 border-black pt-8">
          <p className="font-mono text-xs uppercase tracking-widest text-black/50">
            Other policies
          </p>
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
            {LEGAL_ROUTES.map((r) => (
              <Link
                key={r.to}
                to={r.to}
                className="text-sm font-medium underline underline-offset-4 hover:text-accent"
              >
                {r.label}
              </Link>
            ))}
          </div>
        </nav>
      </article>
    </main>
  );
}

export function Clause({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-extrabold uppercase tracking-tight sm:text-2xl">{heading}</h2>
      <div className="mt-4 space-y-4 text-sm leading-relaxed text-black/80 sm:text-base [&_a]:underline [&_a]:underline-offset-4 [&_li]:ml-5 [&_li]:list-disc [&_ul]:space-y-2">
        {children}
      </div>
    </section>
  );
}
